import React, { Fragment, useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import QueryString from 'query-string';
import { Link } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import Text, { Inline } from 'components/base/Text';
import Table from 'components/base/Table';
import { getColor } from 'common/constants/_colors';
import * as TrackingService from 'common/services/tracking.service';
import { matchContributorMetricsToContributor } from 'common/services/contributors.service';
import AppRouter from 'common/AppRouter';
import { metricNames } from '@linearb/linjs-common';
import {
  convertMetricsNameToV3,
  getMetricsV3
} from 'common/services/metrics.service';
import { Flex } from './base/layout-components';
import MultipleAvatars from './MultipleAvatars';
import { HyperLink } from './base/HyperLink';
import { TRACKING, SEARCH_DATE_FORMAT } from 'common/constants/constants';
import useMetricsCalculationConfig from '../views/root/views/home/hooks/useMetricsCalculationConfig';

const LinkWithoutUnderline = styled(Link)`
  text-decoration: none;
  color: ${getColor('black')};
`;

const getData = (contributors, metrics, currentIteration) => {
  return matchContributorMetricsToContributor(contributors, metrics).map(
    contributorMetric => {
      const {
        contributor_id: contributorId = 0,
        name,
        username,
        avatarUrl
      } = contributorMetric;

      const reviews = _.get(contributorMetric, metricNames.REVIEWS, '-');
      const doneCount = _.get(
        contributorMetric,
        metricNames.DONE_BRANCHES,
        '-'
      );
      const activeCount = _.get(
        contributorMetric,
        metricNames.ACTIVE_BRANCHES,
        '-'
      );
      const contribToLink = {
        pathname: AppRouter.BRANCH.path,
        search: QueryString.stringify({
          metric_name: metricNames.ACTIVE_BRANCHES,
          contributors: contributorId,
          time_range: [
            moment(currentIteration.start_date).format(SEARCH_DATE_FORMAT),
            moment
              .min(moment(currentIteration.end_date), moment())
              .format(SEARCH_DATE_FORMAT)
          ].join(',')
        })
      };

      const tooltipTitle = `View ${
        name ? name.split(' ', 1)[0] : username
      }’s active branches`;
      const nameCol = (
        <Link
          to={contribToLink}
          style={{ textDecoration: 'none', color: getColor('black') }}
        >
          <Tooltip title={tooltipTitle}>
            <Flex items='center'>
              <MultipleAvatars changeCursor avatars={[{ url: avatarUrl }]} />
              <Inline size='large' pl='0.5rem' weight={400}>
                <Text
                  size='large'
                  pl='0.5rem'
                  weight={400}
                  ellipsisAfter='13rem'
                  cursor='pointer'
                >
                  {name ? name : username}
                </Text>
              </Inline>
            </Flex>
          </Tooltip>
        </Link>
      );

      return {
        name: nameCol,
        [metricNames.ACTIVE_BRANCHES]: (
          <Flex items='center'>
            {activeCount !== '-' ? (
              <LinkWithoutUnderline
                to={{
                  pathname: AppRouter.BRANCH.path,
                  search: QueryString.stringify({
                    metric_name: metricNames.ACTIVE_BRANCHES,
                    contributors: contributorId,
                    time_range: [
                      moment(currentIteration.start_date).format(
                        SEARCH_DATE_FORMAT
                      ),
                      moment
                        .min(moment(currentIteration.end_date), moment())
                        .format(SEARCH_DATE_FORMAT)
                    ].join(',')
                  })
                }}
              >
                <Inline size='large' pr='0.5rem' weight={400}>
                  {activeCount}
                </Inline>
              </LinkWithoutUnderline>
            ) : (
              <Inline size='large' pr='0.5rem' weight={400}>
                -
              </Inline>
            )}
          </Flex>
        ),
        [metricNames.DONE_BRANCHES]: (
          <Flex items='center'>
            {doneCount !== '-' ? (
              <LinkWithoutUnderline
                to={{
                  pathname: AppRouter.BRANCH.path,
                  search: QueryString.stringify({
                    metric_name: metricNames.DONE_BRANCHES,
                    contributors: contributorId,
                    time_range: [
                      moment(currentIteration.start_date).format(
                        SEARCH_DATE_FORMAT
                      ),
                      moment
                        .min(moment(currentIteration.end_date), moment())
                        .format(SEARCH_DATE_FORMAT)
                    ].join(',')
                  })
                }}
              >
                <Inline size='large' pr='0.5rem' weight={400}>
                  {doneCount}
                </Inline>
              </LinkWithoutUnderline>
            ) : (
              <Inline size='large' pr='0.5rem' weight={400}>
                -
              </Inline>
            )}
          </Flex>
        ),
        [metricNames.REVIEWS]: (
          <Flex items='center'>
            <Inline size='large' pr='0.5rem' weight={400}>
              {reviews}
            </Inline>
          </Flex>
        )
      };
    }
  );
};

const tableCellProps = { style: { borderRight: 0, borderLeft: 0 } };
const getColumns = isRetrospect => [
  { name: 'NAME', dataPoint: 'name', tableCellProps },
  {
    name: isRetrospect ? 'CARRYOVER' : 'WIP',
    dataPoint: metricNames.ACTIVE_BRANCHES,
    isSortedCol: true,
    tableCellProps
  },
  {
    name: 'DONE',
    dataPoint: metricNames.DONE_BRANCHES,
    isSortedCol: true,
    tableCellProps
  },
  {
    name: 'REVIEWS',
    dataPoint: metricNames.REVIEWS,
    isSortedCol: true,
    tableCellProps
  }
];

const ContributorsRoster = ({
  contributors,
  currentIteration,
  isRetrospect = false,
  dates
}) => {
  const contCount = 6;
  const [contributorsMetrics, setContributorsMetrics] = useState({
    items: [],
    total: 0
  });
  const { metricsCalculationStrategy } = useMetricsCalculationConfig();

  const data = getData(
    contributors,
    contributorsMetrics.items,
    currentIteration
  );
  const columns = getColumns(isRetrospect);

  const { to, from } = dates;

  const [order, setOrder] = useState({
    by: metricNames.DONE_BRANCHES,
    dir: 'desc'
  });

  const getContributorsMetrics = async () => {
    try {
      const requestedMetrics = [
        metricNames.ACTIVE_BRANCHES,
        metricNames.DONE_BRANCHES,
        metricNames.REVIEWS
      ];
      const timeFrame = {
        after: from,
        before: to
      };

      const options = {
        order_by: {
          [order.by]: order.dir
        },
        group_by: 'contributor',
        roll_up: 'custom'
      };

      const metricsV3Data = await getMetricsV3({
        ...options,
        time_ranges: [timeFrame],
        limit: 25,
        requested_metrics: convertMetricsNameToV3(
          requestedMetrics,
          metricsCalculationStrategy
        )
      });
      const mappedMetrics = metricsV3Data
        .map(({ group, data }) =>
          data.map(teamData => ({ ...teamData, contributor_id: group }))
        )
        .flat();
      setContributorsMetrics({ items: mappedMetrics });
    } catch (e) {
      setContributorsMetrics({
        items: [],
        total: 0
      });
    }
  };

  useEffect(() => {
    getContributorsMetrics();
  }, [dates, order]);

  return (
    <Fragment>
      <Flex direction='column' width='100%' height='100%' pt='small'>
        <Flex grow={1}>
          <Table
            sortBy={order.by}
            sortOrder={order.dir}
            onSort={(colName, order) => {
              setOrder({
                by: colName,
                dir: order
              });
            }}
            columns={columns}
            data={data.slice(0, contCount)}
            lineHighlightOnHover={true}
          ></Table>
        </Flex>

        <Flex direction='row-reverse' width='100%' pt='small'>
          <HyperLink
            href={`/contributors/individuals?time=${currentIteration.start_date},${currentIteration.end_date}`}
            onClick={() =>
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.DASHBOARD,
                page_tab: TRACKING.PAGE_TABS.CONTRIBUTORS,
                click_type: TRACKING.CLICK.ALL_CONTRIBUTORS
              })
            }
          >
            All contributors
          </HyperLink>
        </Flex>
      </Flex>
    </Fragment>
  );
};

export default ContributorsRoster;
